import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import CircularProgress from './CircularProgress';
import { checkTokenSendRequest } from '@/utils/apiService';
import { Career } from '@/utils/types';
import { ArrowRight, Star, X } from 'lucide-react';

type CareerModalProps = {
    isOpen: boolean;
    onClose: () => void;
    uuid: string | null;
    careerCode: string | null;
    selectedCareer: Career | null;
};


export const CareerModal: React.FC<CareerModalProps> = ({ isOpen, onClose, uuid, careerCode, selectedCareer }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [career, setCareer] = useState<Career | null>(null)
    const [shortMode, setShortMode] = useState(false);

    const fetchData = useCallback((uuid: string | null, code: string) => {
        setIsLoading(true);
        var url = `/api/result/career/`
        if (uuid === null) {
            url = `/api/career/`
            setShortMode(true)
        }
        checkTokenSendRequest("POST", url, { uuid, code })
            .then((data) => {
                setCareer(data)
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);


    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            if (careerCode) {
                fetchData(uuid, careerCode);
            }
            if (selectedCareer) {
                setCareer(selectedCareer)
                setIsLoading(false);
            }
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, fetchData, careerCode, uuid, selectedCareer]);

    if (!isOpen) return null;
    if (!career) return null;
    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 overflow-hidden backdrop-blur">
            <div className="fixed inset-0 fade-in h-full bg-purple-50 dark:bg-dark-500 bg-opacity-60" onClick={onClose}></div>
            <div className="bg-white dark:bg-dark-700 relative rounded-xl shadow-lg z-10 max-w-4xl mx-4 w-full overflow-hidden bg-opacity-100 ">
                <div className='flex flex-row justify-between items-center sticky top-0 backdrop-blur w-full px-5 pt-5 z-20 pb-5 bg-opacity-80 bg-purple-500 dark:bg-purple-700 text-white '>
                    <div className='font-semibold ps-3'>{isLoading ? ("") : (career.name)}</div>
                    <button onClick={onClose} className='text-2xl text-gray-300'>
                        <X size={20}/>
                    </button>
                </div>
                {isLoading ? (
                    <div className="flex justify-center items-center h-40">
                        <div className="loader"></div>
                        <p>Уншиж байна...</p>
                    </div>
                ) : (
                    <div className='max-h-[70vh] overflow-y-auto '>
                        <div className='md:p-8 p-4'>
                            <div className='grid grid-cols-1 gap-4 md:gap-1 p-3 border border-purple-500 dark:border-purple-400 rounded-lg'>
                                {!shortMode && (
                                    <div className='flex items-center'>
                                        <div className='font-semibold w-1/2 md:text-lg text-sm text-slate-600 dark:text-dark-200'>Тохироц:</div>
                                        <div className='w-1/2 flex'>
                                            {Array.from({ length: career.star_count }).map((_, index) => (
                                                <Star key={index} fill='#f5cb45' size={20} className='text-yellow-500'/>
                                            ))}
                                            {Array.from({ length: 5 - career.star_count }).map((_, index) => (
                                                <Star key={index} size={20} className='text-yellow-500'/>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {career.avg_salary ? (
                                    <div className='flex items-center'>
                                        <div className='font-semibold w-1/2 md:text-lg text-sm text-slate-600'>Цалин:</div>
                                        <div className='w-1/2 italic text-purple-500 font-semibold md:text-lg text-sm'>{career.min_salary.toLocaleString()}₮ - {career.max_salary.toLocaleString()}₮</div>
                                    </div>) : (<></>)}

                                {career.time_commitment && (<div className='flex items-center'>
                                    <div className='font-semibold w-1/2 md:text-lg text-sm text-slate-600 dark:text-white'>Бэлтгэл шаардах хугацаа:</div>
                                    <div className='w-1/2 text-purple-500 dark:text-purple-400 font-semibold  md:text-md text-lg'>{career.time_commitment} жил</div>
                                </div>)}


                            </div>

                            <div className='font-bold mt-5 mb-1 '>Ажлын байрны тодорхойлолт:</div>
                            <div className='text-sm ms-3 mt-3' dangerouslySetInnerHTML={{ __html: career.description }}/>
                            <div className='font-bold mt-5 mb-1'>Гүйцэтгэх үүрэг:</div>
                            <div className='text-sm ms-3 mt-3' dangerouslySetInnerHTML={{ __html: career.objective }} />
                            {career.avg_salary ? (
                                <>
                                    <div className='font-bold mt-5 mb-1'>Цалин:</div>

                                    <div className="grid grid-cols-2 w-full border border-gray-300 rounded-lg text-sm mt-4">
                                        <div className="border-b border-r border-gray-300 p-2 font-semibold">Цалингийн доод хэмжээ</div>
                                        <div className="border-b border-gray-300 p-2 text-end text-purple-500 dark:text-purple-400 font-semibold italic">
                                            {career.min_salary ? `${career.min_salary.toLocaleString()} ₮` : 'Мэдээлэл байхгүй'}
                                        </div>

                                        <div className="border-b border-r border-gray-300 p-2 font-semibold">Дундаж цалин</div>
                                        <div className="border-b border-gray-300 p-2 text-end text-purple-500 dark:text-purple-400 font-semibold italic">
                                            {career.avg_salary ? `${career.avg_salary.toLocaleString()} ₮` : 'Мэдээлэл байхгүй'}
                                        </div>

                                        <div className="border-r border-gray-300 p-2 font-semibold">Цалингийн дээд хэмжээ</div>
                                        <div className="p-2 text-end text-purple-500 dark:text-purple-400 font-semibold italic">
                                            {career.max_salary ? `${career.max_salary.toLocaleString()} ₮` : 'Мэдээлэл байхгүй'}
                                        </div>
                                    </div>

                                    <div className='mt-2 italic text-red-400 text-sm text-center mx-2'>
                                        Дээрх мэдээлэл нь зах зээл дээрх ойролцоо утгатай тодорхойлогдсон хөдөлмөрийн үнэлгээ тул та нэмж судалгаа хийнэ үү.
                                    </div>
                                </>
                            ) : (<></>)}


                            {!shortMode && (
                            <div className='flex border-t border-gray-300 my-5'></div>)}

                            {career.courses.length > 0 && (<>
                                <div className='font-bold mt-5 mb-1'>Мэргэжил эзэмшүүлэх хөтөлбөр ба элсэлтийн ерөнхий шалгалт</div>
                                <div className='ms-3 space-y-4'>
                                    {career.courses.map((item, index) => (
                                        <div key={index} className=' border-t mt-6'>
                                            <div className='grid grid-cols-1 gap-2 mt-4 text-sm'>
                                                <div className='flex items-center'>
                                                    <div className='w-1/2 font-semibold'>Хөтөлбөрийн нэр:</div>
                                                    <div className='w-1/2 italic text-purple-500 dark:text-purple-400 font-semibold'>{item.name}</div>
                                                </div>
                                                <div className='flex items-center'>
                                                    <div className='w-1/2 font-semibold'>Сурахад шаардлагатай хугацаа:</div>
                                                    <div className='w-1/2 italic text-purple-500 dark:text-purple-400 font-semibold'>{item.study_year} жил</div>
                                                </div>
                                            </div>


                                            <div className='mt-2 text-sm font-semibold'>Сургууль/Боловсролын төв:</div>

                                            <div className='mt-2 grid md:grid-cols-2 grid-cols-1 text-xs gap-2'>
                                                {item.schools.map((school: any, index: number) => (
                                                    <div key={index} className='flex flex-col-2 border border-gray-200 rounded-lg p-3 gap-4 md:gap-1'>
                                                        <div className='w-1/6'>
                                                            <div className="flex-shrink-0 h-[40px] w-[40px] rounded-full overflow-hidden">
                                                                <Image src={school.display_image} alt="item image" width={40} height={40} />
                                                            </div>
                                                        </div>
                                                        <div className='w-5/6 flex flex-col'>
                                                            <div className='font-bold text-sm'>{school.name}</div>
                                                            {school.one_credit && <>
                                                                <div className='mt-2 text-gray-900 dark:text-white'>Нэг кредитийн төлбөр:</div>
                                                                <div className='font-semibold'>{school.one_credit}</div>
                                                            </>}
                                                            {school.total_credit && <>
                                                                <div className='mt-2 text-gray-900 dark:text-white'>Ойролцоогоор нийт төлөх:</div>
                                                                <div className='font-semibold'> {school.total_credit}</div>
                                                            </>}
                                                            <div className='mt-3'><Link href={`/myeducation/${school.pk}`} className="text-blue-500 dark:text-blue-400 font-semibold flex flex-row items-center">Дэлгэрэнгүй <ArrowRight size={12}/></Link></div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className='mt-2 text-sm font-semibold'>Элсэн ороход тавигдах шаардлага:</div>
                                            <div className='text-center mt-2 text-xs'>
                                                <div className="grid grid-cols-[auto,1fr,1fr,1fr] w-full border-t border-r border-l border-gray-300 rounded-lg overflow-hidden">
                                                    <div className="p-2 font-bold border-b border-gray-300 rounded-tl-lg grid-flow-dense"></div>
                                                    <div className="p-2 font-bold border-b border-l border-gray-300">Үндсэн хичээл</div>
                                                    <div className="p-2 font-bold border-b border-l border-gray-300">Дагалдах хичээл</div>
                                                    <div className="p-2 font-bold border-b border-l border-gray-300 rounded-tr-lg">Оноо</div>
                                                    <React.Fragment>
                                                        <div className="p-2 text-end border-b border-gray-300">1</div>
                                                        {item.primary_subject_choices.map((subject: any, index: any) => (<div key={index} className="p-2 border-b border-l border-gray-300">{subject.name}</div>))}
                                                        {item.primary_subject_choices.length < 2 && (<div className="p-2 border-b border-l border-gray-300"></div>)}
                                                        <div  className="p-2 border-b border-l border-gray-300">{item.primary_subject_point}</div>
                                                    </React.Fragment>
                                                    <React.Fragment>
                                                        <div className="p-2 text-end border-b border-gray-300">2</div>
                                                        {item.secondary_subject_choices.map((subject: any, index: any) => (<div key={index} className="p-2 border-b border-l border-gray-300">{subject.name}</div>))}
                                                        {item.secondary_subject_choices.length < 2 && (<div className="p-2 border-b border-l border-gray-300"></div>)}
                                                        <div  className="p-2 border-b border-l border-gray-300">{item.secondary_subject_point}</div>
                                                    </React.Fragment>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>)}

                            {!shortMode && (
                                <>
                                    <div className='font-bold mt-5 mb-1'>Энэ мэргэжил танд хэр зэрэг тохирох вэ?</div>
                                    <div className='ms-3'>
                                        <div className='mt-2 text-white text-sm'>Дараах хэсэгт <span className='font-bold'>{career.name}</span> ийн ажилд таны онцлог хэр тохирч байгааг харуулав. </div>
                                        {/* <div className="grid grid-cols-5 text-center p-0 text-xs my-4">
                                            <div className="flex justify-center items-center flex-col">
                                                <div className='w-[45px] h-[45px]'>
                                                    <CircularProgress
                                                        size={45}
                                                        strokeWidth={8}
                                                        progress={10}
                                                    ></CircularProgress>
                                                </div>
                                                <div className='text-[12px] text-gray-800 mt-2'>
                                                    Маш бага илрэл
                                                </div>
                                            </div>

                                            <div className="flex justify-center items-center flex-col">
                                                <div className='w-[45px] h-[45px]'>
                                                    <CircularProgress
                                                        size={45}
                                                        strokeWidth={8}
                                                        progress={25}
                                                    ></CircularProgress>
                                                </div>
                                                <div className='text-[12px] text-gray-800 mt-2'>
                                                    Бага илрэл
                                                </div>
                                            </div>

                                            <div className="flex justify-center items-center flex-col">
                                                <div className='w-[45px] h-[45px]'>
                                                    <CircularProgress
                                                        size={45}
                                                        strokeWidth={8}
                                                        progress={50}
                                                    ></CircularProgress>
                                                </div>
                                                <div className='text-[12px] text-gray-800 mt-2'>
                                                    Дунд илрэл
                                                </div>
                                            </div>


                                            <div className="flex justify-center items-center flex-col">
                                                <div className='w-[45px] h-[45px]'>
                                                    <CircularProgress
                                                        size={45}
                                                        strokeWidth={8}
                                                        progress={75}
                                                    ></CircularProgress>
                                                </div>
                                                <div className='text-[12px] text-gray-800 mt-2'>
                                                    Өндөр илрэл
                                                </div>
                                            </div>

                                            <div className="flex justify-center items-center flex-col">
                                                <div className='w-[45px] h-[45px]'>
                                                    <CircularProgress
                                                        size={45}
                                                        strokeWidth={8}
                                                        progress={100}
                                                    ></CircularProgress>
                                                </div>
                                                <div className='text-[12px] text-gray-800 mt-2'>
                                                    Маш өндөр илрэл
                                                </div>
                                            </div>
                                        </div> */}

                                        <div>
                                            <div className='text-sm font-bold my-2'>Зан төлөв:</div>
                                            <div className='grid grid-cols-1 ms-3 gap-2'>
                                                {career.personality_percentages.map((item, index) => (
                                                    <div key={index} className='flex flex-row border border-gray-300 rounded p-3 gap-3'>
                                                        <CircularProgress
                                                            progress={item.percentage}
                                                            category={item.category}
                                                        ></CircularProgress>
                                                        <div className='text-sm'>
                                                            <div className='font-bold'>{item.name}</div>
                                                            <div className='mt-1'>{item.description}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <div className='text-sm font-bold my-2'>Сонирхол</div>
                                            <div className='grid grid-cols-1 ms-3 gap-2'>
                                                {career.interest_percentages.map((item, index) => (
                                                    <div key={index} className='flex flex-row border border-gray-300 rounded p-3 gap-3'>
                                                        <CircularProgress
                                                            progress={item.percentage}
                                                            category={item.category}
                                                        ></CircularProgress>
                                                        <div className='text-sm'>
                                                            <div className='font-bold'>{item.name}</div>
                                                            <div className='mt-1'>{item.description}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <div className='text-sm font-bold my-2'>Чадвар</div>
                                            <div className='grid grid-cols-1 ms-3 gap-2'>
                                                {career.skill_percentages.map((item, index) => (
                                                    <div key={index} className='flex flex-row border border-gray-300 rounded p-3 gap-3'>
                                                        <CircularProgress
                                                            progress={item.percentage}
                                                            category={item.category}
                                                        ></CircularProgress>
                                                        <div className='text-sm'>
                                                            <div className='font-bold'>{item.name}</div>
                                                            <div className='mt-1'>{item.description}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <div className='text-sm font-bold my-2'>Амьдралын зорилго</div>
                                            <div className='grid grid-cols-1 ms-3 gap-2'>
                                                {career.life_meaning_percentages.map((item, index) => (
                                                    <div key={index} className='flex flex-row border border-gray-300 rounded p-3 gap-3'>
                                                        <CircularProgress
                                                            progress={item.percentage}
                                                            category={item.category}
                                                        ></CircularProgress>
                                                        <div className='text-sm'>
                                                            <div className='font-bold'>{item.name}</div>
                                                            <div className='mt-1'>{item.description}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                        </div>

                    </div>
                )}

            </div>
        </div>
    );
};
